import { useState, useMemo } from 'react'
import Link from 'next/link'
import NewWithdrawal from 'components/NewWithdrawal'
import NewAnticipation from 'components/NewAnticipation'
import useToastContext from "@byma/shared/hooks/useToastContext";
import Head from 'next/head'
import { useRouter } from 'next/router'
import Layout from 'components/Layout'
import Statistic from '@byma/shared/components/Statistic'
import Tabs from '@byma/shared/components/Tabs'
import EventFilter from '@byma/shared/components/EventFilter'
import Loader from '@byma/shared/components/Loader'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useSWR, { useSWRConfig } from 'swr'
import dayjs from 'dayjs'
import useFilters from '@byma/shared/hooks/useFilters'

import jsPDF from 'jspdf'
import { receivableTypeParser, withdrawalStatusParser, anticipationStatusParser, methodParser, money } from '@byma/shared/helpers/formatters'

function PaymentMethodsOverview({perMethod}) {
    return (
        <div className="overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Método
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Transações
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor em ingressos
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Taxas Produtor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custos de Emissão
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Rebate
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Receita
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {perMethod?.sort((a,b) => b.total_amount_cents - a.total_amount_cents).map((item, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className="text-left font-bold text-sm">{methodParser[item.method]} {item.installments}x</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{item.count}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.order_amount_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.producer_amount_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.emission_price_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money((item.customer_amount_cents - item.take_amount_cents + item.producer_amount_cents ) / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.revenue/ 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white text-left">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {perMethod.reduce((acc, cur) => acc += cur.count, 0)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.order_amount_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.producer_amount_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.emission_price_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents), 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(perMethod.reduce((acc, cur) => acc += cur.revenue, 0) / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function downloadComprovante(withdrawal) {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const innerLeft = margin + 5
    const cardLeft = margin
    const cardRight = pageWidth - margin
    const cardWidth = cardRight - cardLeft

    const pixKeyTypeLabels = { CPF: 'CPF', CNPJ: 'CNPJ', EMAIL: 'E-mail', PHONE: 'Telefone', RANDOM: 'Chave aleatória' }

    const approvalEntry = withdrawal.changelog?.find(c => c.status === 'approved')
    const approvalDate = approvalEntry ? dayjs(approvalEntry.updated_at).format('DD/MM/YYYY [–] HH:mm:ss') : dayjs(withdrawal.updated_at).format('DD/MM/YYYY [–] HH:mm:ss')
    const requestDate = dayjs(withdrawal.created_at).format('DD/MM/YYYY [–] HH:mm:ss')

    // Card background
    doc.setFillColor(241, 243, 245)
    doc.roundedRect(cardLeft, 10, cardWidth, 275, 4, 4, 'F')

    // Green header bar
    doc.setFillColor(56, 161, 105)
    doc.roundedRect(cardLeft, 10, cardWidth, 13, 4, 4, 'F')
    doc.rect(cardLeft, 17, cardWidth, 6, 'F')

    // Header checkmark circle
    doc.setFillColor(255, 255, 255)
    doc.circle(innerLeft + 3, 16.5, 3.2, 'F')
    doc.setFillColor(56, 161, 105)
    doc.circle(innerLeft + 3, 16.5, 2.5, 'F')
    // Draw checkmark with lines
    doc.setDrawColor(255, 255, 255)
    doc.setLineWidth(0.6)
    doc.line(innerLeft + 1.5, 16.5, innerLeft + 2.7, 17.7)
    doc.line(innerLeft + 2.7, 17.7, innerLeft + 4.5, 15.5)

    // Header text
    doc.setFontSize(10)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(255, 255, 255)
    doc.text('COMPROVANTE DE TRANSFERÊNCIA', innerLeft + 10, 18)

    // White content area
    doc.setFillColor(255, 255, 255)
    doc.roundedRect(cardLeft + 2, 25, cardWidth - 4, 246, 3, 3, 'F')

    let y = 31
    const contentLeft = cardLeft + 10

    // BYMA logo
    doc.addImage('https://res.cloudinary.com/htkavmx5a/image/upload/c_scale,f_auto,h_120,q_auto:good/v1690549243/BYMA_LOGO_ESCRITO_ORANGE_kztj5z.png', contentLeft, y, 35, 9)
    y += 18

    // VALOR TRANSFERIDO label
    doc.setFontSize(8)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(56, 161, 105)
    doc.text('VALOR TRANSFERIDO', contentLeft, y)
    y += 9

    // Amount value
    doc.setFontSize(22)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(33, 37, 41)
    doc.text(money(withdrawal.amountCents / 100), contentLeft, y)
    y += 8

    // Green badge
    const badgeText = 'Transferência aprovada'
    doc.setFontSize(8)
    doc.setFont(undefined, 'normal')
    const badgeTextWidth = doc.getTextWidth(badgeText)
    const badgeWidth = badgeTextWidth + 8
    doc.setFillColor(220, 252, 231)
    doc.roundedRect(contentLeft, y - 3, badgeWidth, 7, 2, 2, 'F')
    doc.setTextColor(34, 139, 34)
    doc.text(badgeText, contentLeft + 5, y + 1.5)
    doc.setFillColor(34, 139, 34)
    doc.circle(contentLeft + 3, y + 0.5, 1, 'F')
    y += 14

    // Helper: draw a section separator
    function drawSeparator() {
        doc.setDrawColor(225, 228, 232)
        doc.setLineWidth(0.3)
        doc.line(contentLeft, y, cardRight - 10, y)
        y += 6
    }

    // Helper: draw a field (label + value)
    function drawField(label, value) {
        doc.setFontSize(7)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(140, 145, 150)
        doc.text(label.toUpperCase(), contentLeft, y)
        y += 5
        doc.setFontSize(11)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(33, 37, 41)
        doc.text(String(value || '—'), contentLeft, y)
        y += 8
    }

    // Section 1: IDs and dates
    drawSeparator()
    drawField('ID Saque', '#' + withdrawal._id)
    drawField('Data da solicitação', requestDate)
    drawField('Data da aprovação', approvalDate)

    // Section 2: Event
    drawSeparator()
    drawField('Evento', withdrawal.partyId?.name || '—')

    // Section 3: PIX info
    drawSeparator()
    drawField('Tipo de chave PIX', pixKeyTypeLabels[withdrawal.pixKeyType] || withdrawal.pixKeyType || '—')
    drawField('Chave PIX', withdrawal.pixKey || '—')
    drawField('Titular', withdrawal.pixKeyHolderName || '—')

    // Section 4: Observations
    drawSeparator()
    drawField('Observações', withdrawal.description || '—')

    // Footer separator
    y += 2
    doc.setDrawColor(225, 228, 232)
    doc.setLineWidth(0.3)
    doc.line(contentLeft, y, cardRight - 10, y)
    y += 7

    // Footer text
    doc.setFontSize(7)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(140, 145, 150)
    doc.text('Este comprovante é um registro da transferência realizada pela plataforma BYMA.', pageWidth / 2, y, { align: 'center' })
    y += 5
    doc.text('Gerado em ' + dayjs().format('DD/MM/YYYY [às] HH:mm:ss'), pageWidth / 2, y, { align: 'center' })

    doc.save(`comprovante-saque-${withdrawal._id}.pdf`)
}

function Withdrawals({ withdrawals }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!withdrawals || isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (withdrawals?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum saque registrado
            </div>
        )
    }

    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Status
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Obs
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {withdrawals?.result?.map((withdrawal, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(withdrawal.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{withdrawal.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(withdrawal.amountCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{withdrawal.status === 'pending' || withdrawal.status === 'requested' || withdrawal.status === 'refused' ? '-' : money(withdrawal.costCents/ 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`font-bold text-left text-sm ${withdrawal.status === 'refused' ? 'text-red-500' : ''} ${withdrawal.status === 'pending' ? 'text-blue-500' : ''} ${withdrawal.status === 'requested' ? 'text-gray-500' : ''}`}>
                                    {withdrawalStatusParser[withdrawal.status]}
                                </p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{withdrawal.description}</p>
                            </td>
                            <td className="p-3">
                                {withdrawal.status === 'approved' && (
                                    <button
                                        onClick={() => downloadComprovante(withdrawal)}
                                        className="flex items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap bg-white"
                                        title="Baixar comprovante"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1">
                                            <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                                            <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
                                        </svg>
                                        <p>Comprovante</p>
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function Receivables({ receivables }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!receivables|| isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (receivables?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum recebível registrado
            </div>
        )
    }



    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Tipo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {receivables?.result?.map((receivable, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(receivable.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{receivable.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{receivableTypeParser[receivable.type]}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(receivable.amountCents / 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white text-right">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(receivables?.result, 'amountCents') / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function Anticipations({ anticipations }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    if (!anticipations|| isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }

    if (anticipations?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhuma antecipação registrada 
            </div>
        )
    }

    return (
        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Custo
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Status
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Obs
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {anticipations?.result?.map((anticipation, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(anticipation.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{anticipation.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{money(anticipation.amountCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{anticipation.status === 'pending' || anticipation.status === 'requested' || anticipation.status === 'refused' ? '-' : money(anticipation.costCents/ 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`font-bold text-left text-sm ${anticipation.status === 'refused' ? 'text-red-500' : ''} ${anticipation.status === 'pending' ? 'text-blue-500' : ''} ${anticipation.status === 'requested' ? 'text-gray-500' : ''}`}>
                                    {anticipationStatusParser[anticipation.status]}
                                </p>
                            </td>
                            <td className="p-3">
                                <p className="text-left text-sm">{anticipation.description}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                {/*
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white text-left">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(anticipations?.result, 'amountCents') / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(reportSum(anticipations?.result, 'costCents') / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                        </th>
                    </tr>
                </tfoot>
                */}
            </table>
        </div>
    )
}

function InvoiceItems({ invoiceItems }) {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { mutate } = useSWRConfig()
    const { query, ...router } = useRouter()
    const { queryString } = useFilters({...query, d:'all'})
    const [isLoading, setIsLoading] = useState(false)
    const addToast = useToastContext()

    async function deleteItem(_id) {
        setIsLoading(true)
        try {
            const res = await fetch('/api/invoiceitems/delete', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Auth': token
                },
                body: JSON.stringify({ _id }),
            })
            if (res.status === 200) {
                setIsLoading(false)
                addToast('Custo/receita removido com sucesso')
                console.log(`mutating`, `/api/invoiceitems/getAll?${queryString}`)
                mutate([`/api/invoiceitems/getAll?${queryString}`, null, token])
            } else {
                throw new Error(await res.json())
            }
        } catch (error) {
            setIsLoading(false)
            addToast('Erro ao remover custo/receita', 'error')
            console.error('An unexpected error happened occurred:', error)
        }
    }
    if (!invoiceItems || isLoading) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                <Loader />
            </div>
        )
    }
    if (invoiceItems?.result?.length === 0) {
        return (
            <div className="p-3 flex items-center justify-center h-16">
                Nenhum custo registrado
            </div>
        )
    }
    return (

        <div className="h-96 overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th></th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data Criação
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Item
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Quantidade
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor unitário
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {invoiceItems?.result?.map((invoiceItem, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3 flex items-center justify-center">
                                <button onClick={() => deleteItem(invoiceItem._id)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg> 
                                </button>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm`}>{dayjs(invoiceItem.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{invoiceItem.partyId.name}</p>
                            </td>
                            <td className="p-3">
                                <p className="font-bold text-left text-sm">{invoiceItem.description}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{invoiceItem.quantity}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm ${invoiceItem.unitPriceCents > 0 ? 'text-green-700' : 'text-red-700'}`}>{money(invoiceItem.unitPriceCents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className={`text-sm ${invoiceItem.totalCents > 0 ? 'text-green-700' : 'text-red-700'}`}>{money(invoiceItem.totalCents / 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-3 sticky top-0 bg-white text-right">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            {money(invoiceItems.result.reduce((acc, cur) => acc += cur.totalCents, 0) / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function reportSum(report, key) {
    if (!report) return 0
    return report.reduce((acc, cur) => acc += cur[key], 0)
}

export default function Invoices() {
    const session = useAuth()
    const { query, ...router } = useRouter()
    const { token, user: currentUser } = session

    const { queryString, rawFilters } = useFilters({...query, d:'all'})

    const { data: paymentReport, error: paymentReportError } = useSWR(rawFilters?.parties?.length && token ? [`/api/payments/report?fullReport=true&${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: invoiceItems } = useSWR(rawFilters?.parties?.length && token ? [`/api/invoiceitems/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: receivables } = useSWR(rawFilters?.parties?.length && token ? [`/api/receivables/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: anticipations } = useSWR(rawFilters?.parties?.length && token ? [`/api/anticipations/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: withdrawals } = useSWR(rawFilters?.parties?.length && token ? [`/api/withdrawals/getAll?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })

    const perMethod = useMemo(() => {
        if (!paymentReport) {
            return []
        }

        let tmpObj = paymentReport.result.reduce((acc, cur) => {
            const method = `${cur._id.method}-${cur._id.installments}`
            if (!acc[method]) {
                acc[method] = {
                    method: cur._id.method,
                    installments: cur._id.installments,
                    order_amount_cents: cur.order_amount_cents,
                    total_amount_cents: cur.total_amount_cents,
                    producer_amount_cents: cur.producer_amount_cents,
                    customer_amount_cents: cur.customer_amount_cents,
                    take_amount_cents: cur.take_amount_cents,
                    emission_price_cents: cur.emission_price_cents,
                    count: cur.count,
                    revenue: cur.order_amount_cents - cur.producer_amount_cents + (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents) - cur.emission_price_cents,
                }
                return acc
            }
            acc[method].order_amount_cents += cur.order_amount_cents
            acc[method].total_amount_cents += cur.total_amount_cents
            acc[method].producer_amount_cents += cur.producer_amount_cents
            acc[method].customer_amount_cents += cur.customer_amount_cents
            acc[method].emission_price_cents += cur.emission_price_cents
            acc[method].take_amount_cents += cur.take_amount_cents
            acc[method].count += cur.count
            acc[method].revenue += cur.order_amount_cents - cur.producer_amount_cents + (cur.customer_amount_cents - cur.take_amount_cents + cur.producer_amount_cents) - cur.emission_price_cents
            return acc
        }, {})

        return Object.keys(tmpObj).map(k => tmpObj[k])
    }, [paymentReport])

    const costsCents = useMemo(() => (-1 * reportSum(invoiceItems?.result, 'totalCents')) + reportSum(anticipations?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents') + reportSum(withdrawals?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents'), [invoiceItems, anticipations, withdrawals])

    const ignoredMethods = ['cash', 'credit_external', 'debit_external', 'free']

    const remainingTotalCents = (reportSum(perMethod.filter(i => ignoredMethods.indexOf(i.method) < 0 ), 'revenue') - reportSum(perMethod.filter(i => i.method.toLowerCase() === 'cash'), 'emission_price_cents') - reportSum(anticipations?.result.filter(o => ['pending', 'requested'].indexOf(o.status) >= 0), 'amountCents') - reportSum(receivables?.result, 'amountCents') - costsCents - reportSum(perMethod.filter(i => i.method.toLowerCase() === 'cash'), 'producer_amount_cents'))

    const availableToWithdrawalCents = (reportSum(receivables?.result, 'amountCents') - reportSum(withdrawals?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'amountCents') )

    const totalCostCents = costsCents + reportSum(perMethod.filter(i => i.method.toLowerCase() === 'cash'), 'emission_price_cents') + reportSum(perMethod, 'producer_amount_cents')

    if (!rawFilters.parties?.length) {
        return (
            <Layout {...{session}}>
                <div className="flex space-x-3 p-3 shadow w-full overflow-x-auto">
                    <Link href={`/invoices/events-overview`}>
                        <a>
                            <button>
                                <div className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-1">
                                        <path d="M12 9a1 1 0 01-1-1V3c0-.553.45-1.008.997-.93a7.004 7.004 0 015.933 5.933c.078.547-.378.997-.93.997h-5z" />
                                        <path d="M8.003 4.07C8.55 3.992 9 4.447 9 5v5a1 1 0 001 1h5c.552 0 1.008.45.93.997A7.001 7.001 0 012 11a7.002 7.002 0 016.003-6.93z" />
                                    </svg>

                                    <p>
                                        Visão geral
                                    </p>
                                </div>
                            </button>
                        </a>
                    </Link>
                </div>
                <div className="p-3 flex items-start">
                    <div className="p-3 box">
                        <p className="text-lg font-bold mb-3">
                            Selecione um ou mais eventos
                        </p>
                        <p className="text-sm text-gray-700 mb-3">
                            Para visualizar o relatório financeiro, selecione um ou mais eventos na lista abaixo. 
                        </p>
                        <EventFilter 
                            token={token} 
                            filteredEvents={rawFilters.parties} 
                            defaultLabel="Clique para selecionar"
                        />
                    </div>
                </div>
            </Layout>
        )
    }


    console.log('error', paymentReportError?.info)

    return (
        <Layout {...{session}}>
            <div className="p-3">
                <div className="flex md:items-center space-x-3 mb-3 overflow-y-auto py-3">
                    <EventFilter token={token} filteredEvents={rawFilters.parties} />
                </div>

                {paymentReportError ? (
                    <div className="p-3 bg-red-100 text-red-700 rounded mb-3">
                        <p className="font-bold">
                            {paymentReportError?.info?.message === 'Forbidden' ? 
                                    'Você não tem permissão para acessar este relatório'
                                    : 'Erro ao carregar dados'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="p-3 rounded box w-full text-gray-700 mb-6 flex space-x-3 items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                            </svg>

                            <div>
                                <p className="tracking-tight font-bold">Atenção!</p>
                                <p>
                                    Valores e dados descritos aqui não são definitivos até o fechamento do evento e podem passar por alterações a qualquer momento
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row justify-between md:space-x-3 mb-3">
                            <div className="grid grid-cols-2 gap-1 box p-3 w-full">
                                <div className="col-span-2 md:col-span-1">
                                    <p className="mr-3 tracking-tighter">Total em vendas</p>
                                    <p className="text-4xl font-bold tracking-tighter">{money(reportSum(perMethod, 'revenue') / 100)}</p>
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <p className="mr-3 tracking-tighter">Vendas via Byma</p>
                                    <p className="text-xl font-bold tracking-tighter">
                                        {money(reportSum(perMethod.filter(i => ignoredMethods.indexOf(i.method) < 0), 'revenue') / 100)}
                                    </p>
                                </div>
                                <div className="">
                                    <p className="mr-3 tracking-tighter">Total remanescente</p>
                                    <p className="text-xl font-bold tracking-tighter">
                                        {money(remainingTotalCents / 100)}
                                    </p>
                                </div>
                                <div className="">
                                    <p className="mr-3 tracking-tighter">Disponível para saque</p>
                                    <p className="text-xl font-bold tracking-tighter">
                                        {money(availableToWithdrawalCents / 100) }
                                    </p>
                                </div>
                                <div className="">
                                    <NewAnticipation 
                                        availableCents={remainingTotalCents}
                                        disabled={remainingTotalCents <= 0}
                                        eventIds={rawFilters.parties}
                                    />
                                </div>
                                <div className="">
                                    <NewWithdrawal 
                                        availableCents={availableToWithdrawalCents}
                                        disabled={availableToWithdrawalCents <= 0}
                                        eventIds={rawFilters.parties}
                                    />
                                </div>
                            </div>
                            <div className="box p-3 w-full flex flex-col justify-between">
                                <div className="">
                                    <p className="mr-3 tracking-tighter">Custos totais</p>
                                    <p className={`text-2xl font-bold tracking-tighter ${totalCostCents < 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        {totalCostCents < 0 ? '+' : '-' }
                                        {money(Math.abs(totalCostCents) / 100)}
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <p className="tracking-tighter">Taxas produtor</p>
                                        <p className="text-xl font-bold tracking-tighter">
                                            {money(-1 * (reportSum(perMethod, 'producer_amount_cents')) / 100) }
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="tracking-tighter">Outros custos e receitas</p>
                                        <p className="text-xl font-bold tracking-tighter">
                                            {reportSum(invoiceItems?.result, 'totalCents') > 0 ? '+' :'' }
                                            {money(reportSum(invoiceItems?.result, 'totalCents') / 100)}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="tracking-tighter">Custos de emissão</p>
                                        <p className="text-xl font-bold tracking-tighter">
                                            {money(-1 * reportSum(perMethod, 'emission_price_cents') / 100)}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="tracking-tighter">Custos de saques/antecipações</p>
                                        <p className="text-xl font-bold tracking-tighter">
                                            {money(-1 * (reportSum(anticipations?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents') + reportSum(withdrawals?.result.filter(o => ['refused'].indexOf(o.status) < 0), 'costCents')) / 100)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div className="box">
                            <p className="text-md font-bold">Receitas e Taxas</p>
                            <div className="h-96 overflow-y-auto">
                                <PaymentMethodsOverview {...{perMethod}} />
                            </div>
                        </div>
                        <div className="flex flex-col space-y-3">
                            <div className="box">
                                <p className="text-md font-bold">Outros custos/receitas</p>
                                <InvoiceItems {...{invoiceItems}} />
                            </div>
                            <div className="box">
                                <p className="text-md font-bold">Antecipações</p>
                                <div className="flex items-center justify-end space-x-3 mb-3">
                                    <p className="text-sm">Antecipações pendentes:</p>
                                    <p className="text-sm font-bold">
                                        {money(reportSum(anticipations?.result.filter(o => ['pending', 'requested'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                                    </p>
                                </div>
                                <Anticipations {...{anticipations}} />
                                <div className="flex items-center justify-end space-x-3 mb-3">
                                    <p className="text-sm">Total antecipado:</p>
                                    <p className="text-sm font-bold">
                                        {money(reportSum(anticipations?.result.filter(o => ['approved'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                                    </p>
                                </div>
                            </div>
                            <div className="box">
                                <p className="text-md font-bold">Saques realizados</p>
                                <div className="flex items-center justify-end space-x-3 mb-3">
                                    <p className="text-sm">Saques pendentes:</p>
                                    <p className="text-sm font-bold">
                                        {money(reportSum(withdrawals?.result.filter(o => ['requested', 'pending'].indexOf(o.status) >= 0), 'amountCents') / 100)}
                                    </p>
                                </div>
                                <Withdrawals {...{withdrawals}} />
                                <div className="flex items-center justify-end space-x-3 mb-3">
                                    <p className="text-sm">Total sacado:</p>
                                    <p className="text-sm font-bold">
                                        {money(reportSum(withdrawals?.result.filter(o => o.status === 'approved'), 'amountCents') / 100)}
                                    </p>
                                </div>
                            </div>
                            <div className="box">
                                <p className="text-md font-bold">Recebíveis</p>
                                <Receivables {...{receivables}} />
                            </div>
                        </div>
                    </>
                )}


            </div>
        </Layout>
    )
}

